// The band controls' one way to read and write the band (components/bandScope.ts):
// the mixer, the Style panel, the Tempo panel's kit and the Chords menu's
// Backing all go through it, so they show the same band and write to it.

import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { StyleBand, StyleId } from '@/music/style';
import { pickBand } from '@/state/persistence';
import { useStore } from '@/state/store';
import {
  BAND_SCOPE_ALL,
  presetPatch,
  resolveBandScope,
  scopedBandWrites,
  shownBand,
  type BandSplit,
} from './bandScope';

export interface BandEditor {
  split: BandSplit | null;
  /** A label, or BAND_SCOPE_ALL. */
  scope: string;
  setScope: (scope: string) => void;
  /** What the controls show. */
  band: StyleBand;
  /** Fields the labels disagree on, under All. */
  varies: ReadonlySet<keyof StyleBand>;
  edit: (patch: Partial<StyleBand>) => void;
  applyPreset: (id: StyleId) => void;
  /** Back to one band for the whole tune. */
  oneBand: () => void;
}

export function useBandEditor(split: BandSplit | null): BandEditor {
  const live = useStore(useShallow((s) => pickBand(s)));
  const stored = useStore((s) => s.bandScope);
  const setBandScope = useStore((s) => s.setBandScope);
  const scope = resolveBandScope(split, stored);
  const shown = useMemo(() => shownBand(split, scope, live), [split, scope, live]);

  const edit = useCallback(
    (patch: Partial<StyleBand>) => {
      const s = useStore.getState();
      const writes = scopedBandWrites(split, scope, pickBand(s), patch);
      if (writes.live && Object.keys(writes.live).length) s.patchBand(writes.live);
      for (const [label, band] of Object.entries(writes.labels)) {
        if (split?.kind === 'sections') s.assignSectionBand(label, band);
        else s.assignFeelGroove(label, band);
      }
    },
    [split, scope],
  );
  const applyPreset = useCallback(
    (id: StyleId) => {
      if (!split) {
        useStore.getState().applyStyle(id);
        return;
      }
      const patch = presetPatch(id);
      if (patch) edit(patch);
    },
    [split, edit],
  );
  const oneBand = useCallback(() => {
    const s = useStore.getState();
    if (split?.kind === 'sections') s.setSectionsOn(false);
    else if (split?.kind === 'feels') s.setFollowFeelCues(false);
    s.setBandScope(null);
  }, [split]);
  const setScope = useCallback(
    (next: string) => setBandScope(next === BAND_SCOPE_ALL ? null : next),
    [setBandScope],
  );

  return {
    split,
    scope,
    setScope,
    band: shown.band,
    varies: shown.varies,
    edit,
    applyPreset,
    oneBand,
  };
}
